import { extractSvgAttributes, getFavoriteComponents, getPropertyOrFallback } from '../MapLevel/MapLevelHelpers';

/**
 * Helper functions for MapPlace component
 */

/**
 * Generates SVG markup string for all place shapes
 * This allows rendering places without React overhead
 * stroke-width is set via CSS (.place class in MapPlace.scss)
 * @param {Array} places - Array of place objects
 * @param {string} placeFill - Fill color for active places
 * @param {string} placeStroke - Stroke color for active places
 * @param {string} unitFill - Fill color for disabled places (matches disabled units)
 * @param {string} unitStroke - Stroke color for disabled places
 * @param {boolean} markUnpublished - Tag draft places with `place--unpublished`.
 *   Only ever true once the server has granted preview: on the public map a
 *   draft must be indistinguishable, not highlighted (#552).
 * @returns {string} SVG markup string
 */
export const generatePlaceShapesMarkup = (
  places,
  placeFill,
  placeStroke,
  unitFill,
  unitStroke,
  markUnpublished = false,
) => {
  if (!places || places.length === 0) return '';

  const shapeStrings = places.map((place) => {
    const { type, attributes, pin, displayMode } = place;
    if (!type || !attributes) return '';

    const isInvisible = isPlaceInvisible(pin);
    const isDisabled = displayMode === 'disabled';
    let fill;
    let stroke;
    if (isInvisible) {
      fill = 'transparent';
      stroke = 'transparent';
    } else if (isDisabled) {
      fill = unitFill;
      stroke = unitStroke;
    } else {
      fill = placeFill;
      stroke = placeStroke;
    }
    const isUnpublished = markUnpublished && pin?.unpublished === true;
    const className = isInvisible
      ? ''
      : `place${isDisabled ? ' place--disabled' : ''}${isUnpublished ? ' place--unpublished' : ''}`;
    const id = attributes.id || '';

    // Build attribute string - stroke-width comes from CSS
    const attrParts = [];
    if (id) attrParts.push(`data-svg-id="${id}"`);
    if (className) attrParts.push(`class="${className}"`);
    attrParts.push(`fill="${fill}"`);
    attrParts.push(`stroke="${stroke}"`);

    // Add geometry attributes
    if (attributes.d) attrParts.push(`d="${attributes.d}"`);
    if (attributes.points) attrParts.push(`points="${attributes.points}"`);
    if (attributes.x !== undefined) attrParts.push(`x="${attributes.x}"`);
    if (attributes.y !== undefined) attrParts.push(`y="${attributes.y}"`);
    if (attributes.width !== undefined) attrParts.push(`width="${attributes.width}"`);
    if (attributes.height !== undefined) attrParts.push(`height="${attributes.height}"`);
    if (attributes.r !== undefined) attrParts.push(`r="${attributes.r}"`);
    if (attributes.cx !== undefined) attrParts.push(`cx="${attributes.cx}"`);
    if (attributes.cy !== undefined) attrParts.push(`cy="${attributes.cy}"`);
    if (attributes.transform) attrParts.push(`transform="${attributes.transform}"`);

    return `<${type} ${attrParts.join(' ')} />`;
  });

  return shapeStrings.join('');
};

/**
 * Builds props object for place SVG element
 * @param {Object} params - Parameters for building place props
 * @param {string} params.id - SVG element ID
 * @param {boolean} params.isInvisible - Whether place should be invisible
 * @param {string} params.placeStroke - Stroke color for place
 * @param {string} params.placeFill - Fill color for place
 * @param {Object} params.attributes - SVG attributes (points, d, x, y, width, height, r, cx, cy, transform)
 * @returns {Object} Props object for SVG element
 */
export const buildPlaceProps = ({
  id,
  isInvisible,
  placeStroke,
  placeFill,
  attributes,
}) => {
  const svgAttributes = extractSvgAttributes(attributes);

  const props = {
    'data-svg-id': id,
    stroke: isInvisible ? 'transparent' : placeStroke,
    fill: isInvisible ? 'transparent' : placeFill,
    className: !isInvisible ? 'place' : '',
  };

  // Add only defined SVG attributes
  // eslint-disable-next-line no-restricted-syntax
  for (const property in svgAttributes) {
    if (svgAttributes.hasOwnProperty(property)) {
      props[property] = svgAttributes[property];
    }
  }

  return props;
};

/**
 * Builds modal header data for place detail modal
 * @param {boolean} showFavorite - Whether to render the favorite button
 * @param {string} uuid - Place UUID
 * @param {Function} FavoriteButton - FavoriteButton component
 * @param {boolean} forceVisible - Show heart when personalization off (active tgai session)
 * @param {Function} onToggle - Called with (nowFavorited) after a toggle (e.g. tgai capture)
 * @param {string} ariaLabel - Accessible name for the heart
 * @returns {Object} Modal header data object
 */
export const buildPlaceModalHeaderData = (
  showFavorite, uuid, FavoriteButton, forceVisible = false, onToggle = null, ariaLabel = null,
) => {
  const components = getFavoriteComponents(
    showFavorite, 'place', uuid, FavoriteButton, forceVisible, onToggle, ariaLabel,
  );

  return {
    titleParts: [],
    tags: [],
    subtitles: [],
    btns: [],
    components,
  };
};

/**
 * Determines if a place marker should show as a link
 * @param {Object} pin - Place pin data
 * @returns {boolean} Whether place is a link
 */
// Use truthy check since Drupal may send "true" or "1" as strings
export const isPlaceLink = (pin) => !!pin?.create_modal;

/**
 * Determines if a place should be invisible
 * @param {Object} pin - Place pin data
 * @returns {boolean} Whether place is invisible
 */
export const isPlaceInvisible = (pin) => pin?.pin_type === 'Invisible SVG';

/**
 * Gets label orientation with fallback
 * @param {Object} pin - Place pin data
 * @returns {string} Label orientation ('left', 'right', 'top', 'bottom')
 */
export const getLabelOrientation = (pin) => getPropertyOrFallback(pin, 'label_orientation', 'left');

/**
 * Gets modal title with fallback
 * @param {Object} pin - Place pin data
 * @returns {string} Modal title
 */
export const getModalTitle = (pin) => getPropertyOrFallback(pin, 'modal_title', '');
